"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.reportTimelineResultSchema = exports.reportTimelineRowSchema = exports.reportTimelineCellSchema = exports.reportTimelineColumnSchema = exports.DEFAULT_TIMELINE = exports.timelineSpecSchema = exports.TIMELINE_MAX_COLUMNS = exports.TIMELINE_EXTRA_KINDS = exports.TIMELINE_EXTRA_DIMENSIONS_FOR = exports.TIMELINE_DIMENSIONS_FOR = exports.TIMELINE_DIMENSIONS = exports.TIMELINE_GRANULARITIES = void 0;
exports.bucketStart = bucketStart;
exports.bucketEnd = bucketEnd;
exports.bucketsBetween = bucketsBetween;
const zod_1 = require("zod");
const dates_1 = require("../dates");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 17 Oct 2026 · THE TIMELINE LAYOUT — *Időbeli*.                            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The third way of reading a report, beside *Összesítő* (grouped totals for a chart) and *Lista*
 * (one row per record). Rows are one or two dimensions, nested; columns are the periods across the
 * report's range — a week, a month or a quarter each — and a row total. It adds up ACROSS a row and
 * never down a column into one figure, which is what the old product's `timelog_report.xlsx` is:
 * *Projekt logolások* (person × line item × month), the three *Ismeretlen* sheets (unknown hours by
 * person, by task, by both) — and Kockázatkezelés's three tabs, which this layout retired.
 *
 * ⚠️ **Extras are columns a row carries beside its periods**: how many distinct tasks a person's
 * unknown hours fell on, and which (*Jegyek száma*, *Jegyek*), or who logged on a task
 * (*Logolók*). A count or a list of one more dimension, per row.
 *
 * ⚠️ **No row cap and no stored snapshot.** Like a list it runs when opened and pages, because the
 * old product's person × line item sheet is seven hundred rows.
 */
exports.TIMELINE_GRANULARITIES = ['week', 'month', 'quarter'];
/**
 * What a timeline's rows can be, per source.
 *
 * ⚠️ `assignee` on a worklog is who LOGGED the hour (`report.service.ts`'s rule for the summary);
 * on Munkaidő it is the person whose day it is — the only thing a working day belongs to.
 */
exports.TIMELINE_DIMENSIONS = [
    'assignee',
    'task',
    'lineitem',
    'contract',
    'project',
    'client',
    'status',
];
exports.TIMELINE_DIMENSIONS_FOR = {
    worklog: ['assignee', 'task', 'lineitem', 'contract', 'project', 'client', 'status'],
    workday: ['assignee'],
};
/** The dimensions an extra column can count or list, per source. */
exports.TIMELINE_EXTRA_DIMENSIONS_FOR = {
    worklog: ['task', 'assignee', 'lineitem', 'project'],
    workday: [],
};
exports.TIMELINE_EXTRA_KINDS = ['count', 'list'];
/**
 * ⚠️ **The longest range a timeline draws**, per granularity — two years of months, two years of
 * weeks, five years of quarters. Past it the table is wider than anybody reads; the server refuses
 * rather than truncating, and says which.
 */
exports.TIMELINE_MAX_COLUMNS = {
    week: 106,
    month: 24,
    quarter: 20,
};
exports.timelineSpecSchema = zod_1.z
    .object({
    rows: zod_1.z.array(zod_1.z.enum(exports.TIMELINE_DIMENSIONS)).min(1).max(2),
    granularity: zod_1.z.enum(exports.TIMELINE_GRANULARITIES),
    extras: zod_1.z
        .array(zod_1.z.object({ kind: zod_1.z.enum(exports.TIMELINE_EXTRA_KINDS), dimension: zod_1.z.enum(exports.TIMELINE_DIMENSIONS) }))
        .max(4),
})
    .refine((value) => new Set(value.rows).size === value.rows.length, {
    error: 'the rows need two different dimensions',
    path: ['rows'],
});
/** What a fresh timeline starts with: one row per person, by month, nothing extra. */
exports.DEFAULT_TIMELINE = {
    rows: ['assignee'],
    granularity: 'month',
    extras: [],
};
/* ── the periods ─────────────────────────────────────────────────────────────────────────────── */
/** The first day of the period a date falls in. */
function bucketStart(date, granularity) {
    if (granularity === 'week')
        return (0, dates_1.wireMonday)(date) ?? date;
    if (granularity === 'month')
        return `${date.slice(0, 7)}-01`;
    const month = Number(date.slice(5, 7));
    const first = month - ((month - 1) % 3);
    return `${date.slice(0, 4)}-${String(first).padStart(2, '0')}-01`;
}
/** The last day of the period starting on `start`. */
function bucketEnd(start, granularity) {
    if (granularity === 'week')
        return (0, dates_1.addWireDays)(start, 6) ?? start;
    const year = Number(start.slice(0, 4));
    const month = Number(start.slice(5, 7)) + (granularity === 'month' ? 1 : 3);
    const next = month > 12
        ? `${String(year + 1)}-${String(month - 12).padStart(2, '0')}-01`
        : `${String(year)}-${String(month).padStart(2, '0')}-01`;
    return (0, dates_1.addWireDays)(next, -1) ?? start;
}
/**
 * Every period a range touches, in order — the timeline's columns.
 *
 * ⚠️ **Whole periods, clipped to the range at the ends.** A range from the 10th shows that month as
 * its first column, holding the 10th onwards; the column's `from` says so, and the screen can mark it.
 */
function bucketsBetween(from, to, granularity) {
    const out = [];
    let start = bucketStart(from, granularity);
    while (start <= to) {
        const end = bucketEnd(start, granularity);
        out.push({ key: start, from: start < from ? from : start, to: end > to ? to : end });
        const next = (0, dates_1.addWireDays)(end, 1);
        if (next === null)
            break;
        start = next;
    }
    return out;
}
/* ── the result ──────────────────────────────────────────────────────────────────────────────── */
exports.reportTimelineColumnSchema = zod_1.z.object({
    /** The period's first day — `YYYY-MM-DD`; the screen names it (week number, month, quarter). */
    key: zod_1.z.string(),
    from: zod_1.z.string(),
    to: zod_1.z.string(),
    /**
     * ⚠️ **Munkaidő only: the period can no longer be logged** — closed by the monthly lock or past
     * the backdating window, asked as an ordinary member would ask it. Kockázatkezelés's *lejárt*.
     */
    lapsed: zod_1.z.boolean(),
});
exports.reportTimelineCellSchema = zod_1.z.object({
    /** The group's identity — a uuid, or `'none'` for the row of what has no value. */
    key: zod_1.z.string(),
    /** Null for `'none'`; the screen words it per dimension, as the summary does. */
    label: zod_1.z.string().nullable(),
    /** A short code beside the label: a task's key, a line item's Jira project or contract code. */
    code: zod_1.z.string().nullable(),
    /** Where clicking it goes, workspace-relative, when it is somewhere. */
    path: zod_1.z.string().nullable(),
});
exports.reportTimelineRowSchema = zod_1.z.object({
    /** The row's keys joined — stable across pages, for React. */
    id: zod_1.z.string(),
    cells: zod_1.z.array(exports.reportTimelineCellSchema),
    /** One per column, as a decimal string; hours and money in the report's units. */
    values: zod_1.z.array(zod_1.z.string()),
    total: zod_1.z.string(),
    /** One per extra: a count as an integer string, a list as comma-separated names. */
    extras: zod_1.z.array(zod_1.z.string()),
});
exports.reportTimelineResultSchema = zod_1.z.object({
    columns: zod_1.z.array(exports.reportTimelineColumnSchema),
    rows: zod_1.z.array(exports.reportTimelineRowSchema),
    /** ⚠️ Across EVERY row the filters leave, not just this page — the footer is the whole report's. */
    totals: zod_1.z.object({ values: zod_1.z.array(zod_1.z.string()), total: zod_1.z.string() }),
    meta: zod_1.z.object({
        next_cursor: zod_1.z.null(),
        total: zod_1.z.number().int().nonnegative(),
        offset: zod_1.z.number().int().nonnegative(),
        limit: zod_1.z.number().int().positive(),
    }),
    currencyCode: zod_1.z.string(),
});
//# sourceMappingURL=timeline.js.map